import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';

// Champagne glass icon with gold liquid
const ChampagneGlassIcon = () => (
  <svg className="w-5 h-5 text-[#3B82F6] dark:text-[#60A5FA]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    {/* Glass stem */}
    <path d="M12 2v20M12 2l-2 4M12 2l2 4" stroke="currentColor" strokeWidth="1.5" />
    {/* Glass bowl */}
    <path d="M6 6h12M6 6l-1 8h14l-1-8" stroke="currentColor" strokeWidth="1.5" />
    {/* Gold liquid */}
    <path d="M7 8h10v4H7z" fill="#EAB308" opacity="0.8" />
    {/* Bubbles */}
    <circle cx="9" cy="10" r="0.5" fill="#EAB308" />
    <circle cx="11" cy="9" r="0.5" fill="#EAB308" />
    <circle cx="13" cy="10" r="0.5" fill="#EAB308" />
    <circle cx="15" cy="9" r="0.5" fill="#EAB308" />
  </svg>
);

const BandzEliteToast = ({ show, onClose }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (show) {
      let timer;
      
      if (isExpanded) {
        // When expanded, stay open for 20 seconds
        timer = setTimeout(() => {
          onClose();
        }, 20000);
      } else {
        // When collapsed, auto-dismiss after 10 seconds
        timer = setTimeout(() => {
          onClose();
        }, 10000);
      }

      return () => clearTimeout(timer);
    } else {
      // Reset expanded state when toast is closed
      setIsExpanded(false);
    }
  }, [show, onClose, isExpanded]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, x: 400, y: -20 }}
          animate={{ opacity: 1, x: 0, y: 0 }}
          exit={{ opacity: 0, x: 400 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className={`fixed top-4 right-4 z-[9999] bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-lg shadow-elevated overflow-hidden cursor-pointer ${isExpanded ? 'w-80' : 'w-auto min-w-[180px]'}`}
          onClick={() => !isExpanded && setIsExpanded(true)}
        >
          {!isExpanded ? (
            // Collapsed view - just icon and "From: Bandz"
            <div className="flex items-center justify-between p-3 gap-3">
              <div className="flex items-center gap-3">
                <ChampagneGlassIcon />
                <span className="text-sm font-medium text-[#111827] dark:text-[#F9FAFB] whitespace-nowrap">From: <span className="text-yellow-500">Bandz</span></span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="text-[#6B7280] dark:text-[#9CA3AF] hover:text-[#111827] dark:hover:text-[#F9FAFB] transition-colors flex-shrink-0"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          ) : (
            // Expanded view - full message
            <>
              {/* Header */}
              <div className="flex items-center justify-between p-3 bg-[#F3F4F6] dark:bg-[#374151] border-b border-[#E5E7EB] dark:border-[#374151]">
                <div className="flex items-center gap-2">
                  <ChampagneGlassIcon />
                  <span className="text-sm font-semibold text-[#111827] dark:text-[#F9FAFB]">New Message</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                  className="text-[#6B7280] dark:text-[#9CA3AF] hover:text-[#111827] dark:hover:text-[#F9FAFB] transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="mb-2">
                  <div className="text-xs text-[#6B7280] dark:text-[#9CA3AF] mb-1">From:</div>
                  <div className="text-sm font-medium text-yellow-500">Bandz</div>
                </div>
                <div className="mb-3">
                  <div className="text-xs text-[#6B7280] dark:text-[#9CA3AF] mb-1">Subject:</div>
                  <div className="text-sm font-semibold text-[#111827] dark:text-[#F9FAFB]">Good Job.</div>
                </div>
                <div className="text-sm text-[#374151] dark:text-[#D1D5DB] leading-relaxed space-y-2">
                  <p className="italic">"Whoever loves discipline loves knowledge, but whoever hates correction is stupid." — Proverbs 12:1</p>
                  <p>Congratulations on your winning streak.</p>
                </div>
              </div>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BandzEliteToast;
